/**
 * Snapshot interpolation for remote entities.
 *
 * Remote entities render INTERP_DELAY_MS in the past, lerped between the two
 * bracketing snapshots. On a snapshot gap (TCP delay) we hold-and-extrapolate
 * for at most MAX_EXTRAPOLATION_MS, then freeze rather than guess
 * (docs/architecture/networking.md "Movement").
 *
 * Pure data + math so it is unit-testable with node:test and usable by both
 * the browser client and the headless proof harness.
 */
import { MAX_EXTRAPOLATION_MS, TICK_MS } from './constants.js';
/** Interpolate angles along the shortest arc. */
export function lerpAngle(a, b, t) {
    let diff = (b - a) % (2 * Math.PI);
    if (diff > Math.PI)
        diff -= 2 * Math.PI;
    if (diff < -Math.PI)
        diff += 2 * Math.PI;
    return a + diff * t;
}
const MAX_BUFFER_POINTS = 64;
export class InterpolationBuffer {
    points = [];
    /** Insert a snapshot point; replaces an existing point for the same tick. */
    push(p) {
        const points = this.points;
        let i = points.length - 1;
        while (i >= 0) {
            const existing = points[i];
            if (existing === undefined || existing.tick <= p.tick)
                break;
            i -= 1;
        }
        const at = points[i];
        if (at !== undefined && at.tick === p.tick) {
            points[i] = p;
        }
        else {
            points.splice(i + 1, 0, p);
        }
        if (points.length > MAX_BUFFER_POINTS) {
            points.splice(0, points.length - MAX_BUFFER_POINTS);
        }
    }
    get size() {
        return this.points.length;
    }
    newestTick() {
        const last = this.points[this.points.length - 1];
        return last === undefined ? null : last.tick;
    }
    /**
     * Sample the entity state at a (fractional) render tick.
     * Returns null when the buffer is empty.
     */
    sample(renderTick) {
        const points = this.points;
        const first = points[0];
        const last = points[points.length - 1];
        if (first === undefined || last === undefined)
            return null;
        if (renderTick <= first.tick) {
            return {
                x: first.x,
                z: first.z,
                facing: first.facing,
                anim: first.anim,
                extrapolatedMs: 0,
                frozen: false,
            };
        }
        if (renderTick >= last.tick) {
            // Beyond the newest snapshot: hold-and-extrapolate, then freeze.
            const prev = points[points.length - 2];
            const aheadMs = (renderTick - last.tick) * TICK_MS;
            const clampedMs = Math.min(aheadMs, MAX_EXTRAPOLATION_MS);
            const frozen = aheadMs > MAX_EXTRAPOLATION_MS;
            if (prev === undefined || last.tick === prev.tick) {
                return {
                    x: last.x,
                    z: last.z,
                    facing: last.facing,
                    anim: last.anim,
                    extrapolatedMs: clampedMs,
                    frozen,
                };
            }
            const dtMs = (last.tick - prev.tick) * TICK_MS;
            const vx = (last.x - prev.x) / dtMs;
            const vz = (last.z - prev.z) / dtMs;
            return {
                x: last.x + vx * clampedMs,
                z: last.z + vz * clampedMs,
                facing: last.facing,
                anim: frozen ? 'idle' : last.anim,
                extrapolatedMs: clampedMs,
                frozen,
            };
        }
        // Interpolate between the bracketing points.
        for (let i = points.length - 1; i >= 1; i -= 1) {
            const a = points[i - 1];
            const b = points[i];
            if (a === undefined || b === undefined)
                continue;
            if (renderTick >= a.tick && renderTick <= b.tick) {
                const span = b.tick - a.tick;
                const t = span === 0 ? 1 : (renderTick - a.tick) / span;
                return {
                    x: a.x + (b.x - a.x) * t,
                    z: a.z + (b.z - a.z) * t,
                    facing: lerpAngle(a.facing, b.facing, t),
                    anim: b.anim,
                    extrapolatedMs: 0,
                    frozen: false,
                };
            }
        }
        // Unreachable given the guards above, but keep the type checker honest.
        return {
            x: last.x,
            z: last.z,
            facing: last.facing,
            anim: last.anim,
            extrapolatedMs: 0,
            frozen: false,
        };
    }
}
//# sourceMappingURL=interpolation.js.map